import { ASSISTANT_STEP, CUSTOMERS_STEP, PAYMENTS_STEP, stepStatus, STORE_DETAILS_STEP } from '@/composables/setupState'

const STEP_LABELS = {
  [STORE_DETAILS_STEP]: 'Confirm your store details',
  [PAYMENTS_STEP]: 'Connect payments',
  [CUSTOMERS_STEP]: 'Import your customers',
  [ASSISTANT_STEP]: 'Meet your assistant'
}

export function setupStepLabel(key) {
  return STEP_LABELS[key] || null
}

export function setupTasks(steps) {
  return (Array.isArray(steps) ? steps : [])
    .filter(Boolean)
    .map(step => ({ key: step.key, complete: Boolean(step.complete), label: setupStepLabel(step.key) }))
    .filter(task => task.label)
}

export function paymentsTone(steps) {
  const step = (Array.isArray(steps) ? steps : []).find(candidate => candidate && candidate.key === PAYMENTS_STEP)
  return step ? stepStatus(step).tone : 'todo'
}

export function setupSurface({ complete, pendingReview, outstandingCount, steps } = {}) {
  return {
    complete: Boolean(complete),
    pendingReview: Boolean(pendingReview),
    outstanding: Number(outstandingCount) || 0,
    payments: paymentsTone(steps),
    tasks: setupTasks(steps)
  }
}

const PAYMENTS_DONE = 'done'
const PAYMENTS_PENDING = 'pending'
const PAYMENTS_ATTENTION = 'attention'

// The two tones that mean checkout is open, so the storefront link is worth
// putting in front of the merchant and the surface can dress itself as settled.
const SETTLED_TONES = ['live', 'review']

export function isSettledTone(tone) {
  return SETTLED_TONES.includes(tone)
}

function detailsLeft(count) {
  return count === 1 ? '1 detail' : `${count} details`
}

// Pending is the only tone that leaves checkout shut with nothing for the merchant
// to do, so everything else that is not done - including Stripe waiting on them -
// belongs in the register that names the problem and hands them a way out.
function paymentsBlocked(surface) {
  return surface.payments !== PAYMENTS_DONE && surface.payments !== PAYMENTS_PENDING
}

// The order is the rule, and all three surfaces borrow it from here: payments is
// the one thing that shuts checkout, so a store in the review queue whose Stripe
// fell over is told what that means before it is told we are looking it over.
// Every other step dropping back leaves nothing to say, and the review stands.
// attention is its own register because blocked's wording is about a store whose
// Stripe is gone. This merchant's Stripe is connected and holding for one thing,
// so telling them to reconnect it describes an account that is not theirs. Where
// the distinction does not change the words, attention falls back to blocked.
function reviewRegister(surface, { blocked, attention, pending, review }) {
  if (surface.payments === PAYMENTS_ATTENTION) return attention || blocked
  if (paymentsBlocked(surface)) return blocked
  if (surface.payments === PAYMENTS_PENDING) return pending
  return review
}

export function setupBadgeCopy(surface) {
  if (surface.pendingReview) {
    return reviewRegister(surface, {
      blocked: { title: 'Payments need you', subtitle: 'Checkout is closed' },
      pending: { title: 'Pending review', subtitle: 'Checkout opens when Stripe is done' },
      review: { title: 'Pending review', subtitle: 'We are checking your store' }
    })
  }

  if (surface.complete) return { title: 'Store setup done', subtitle: 'Nothing pending' }

  return {
    title: 'Finish store setup',
    subtitle: surface.outstanding === 1 ? '1 detail needs you' : `${surface.outstanding} details need you`
  }
}

export function setupBannerCopy(surface) {
  const left = `${detailsLeft(surface.outstanding)} left`

  if (surface.pendingReview) {
    return reviewRegister(surface, {
      blocked: {
        tone: 'blocked',
        title: "Your store can't take orders right now",
        body: 'Your setup is done and we are checking it over, but checkout stays closed until payments are connected.',
        action: { kind: 'open', label: 'Reconnect payments' }
      },
      attention: {
        tone: 'blocked',
        title: 'Stripe needs something from you',
        body: 'Your setup is done and we are checking it over, but checkout stays closed until Stripe has what it is asking for.',
        action: { kind: 'open', label: 'See what Stripe needs' }
      },
      pending: {
        tone: 'pending',
        title: 'Stripe is still checking your details',
        body: 'Your setup is done and we are checking it over. Checkout opens as soon as Stripe is done.',
        action: { kind: 'open', label: 'Finish setup' }
      },
      // Says nothing about checkout: it is already open once payments are
      // connected, and a review the merchant cannot act on must not read as a
      // store shut down.
      review: {
        tone: 'review',
        title: 'Your setup is done and we are checking it over',
        body: 'We look over every new store by hand, so someone at Bottle will be in touch.',
        action: { kind: 'copy', label: 'Copy store link' }
      }
    })
  }

  if (surface.complete) {
    return {
      tone: 'live',
      title: "You're live and ready for orders",
      body: 'Setup is done. Share your store link and the first order will land here.',
      action: { kind: 'copy', label: 'Copy store link' }
    }
  }

  if (surface.payments === PAYMENTS_PENDING) {
    return {
      tone: 'pending',
      title: 'Stripe is still checking your details',
      body: `Checkout opens as soon as Stripe is done. ${left} in your setup.`,
      action: { kind: 'open', label: 'Finish setup' }
    }
  }

  // Named apart from the unconnected merchant because the ask is different: this
  // one has already been through Stripe and is being asked for one more thing.
  if (surface.payments === PAYMENTS_ATTENTION) {
    return {
      tone: 'blocked',
      title: 'Stripe needs something from you',
      body: `Checkout stays closed until you give Stripe what it asked for. ${left} in your setup.`,
      action: { kind: 'open', label: 'Finish setup' }
    }
  }

  if (surface.payments !== PAYMENTS_DONE) {
    return {
      tone: 'blocked',
      title: "Your store can't take orders yet",
      body: `Checkout stays closed until payments are connected. ${left} in your setup.`,
      action: { kind: 'open', label: 'Finish setup' }
    }
  }

  return {
    tone: 'progress',
    title: `Nearly there - ${left}`,
    body: 'Checkout is open. Finish the rest so orders run smoothly.',
    action: { kind: 'open', label: 'Finish setup' }
  }
}

export function setupEmptyStateCopy(surface) {
  const finish = { kind: 'open', label: 'Finish store setup' }

  if (surface.pendingReview) {
    return reviewRegister(surface, {
      blocked: {
        tone: 'blocked',
        title: 'No orders yet',
        body: 'Your setup is done and we are checking it over, but checkout stays closed until payments are connected.',
        action: { kind: 'open', label: 'Reconnect payments' }
      },
      attention: {
        tone: 'blocked',
        title: 'No orders yet',
        body: 'Your setup is done and we are checking it over, but checkout stays closed until Stripe has what it is asking for.',
        action: { kind: 'open', label: 'See what Stripe needs' }
      },
      pending: {
        tone: 'pending',
        title: 'No orders yet',
        body: 'Your setup is done and we are checking it over. Orders land on the calendar above as soon as Stripe is done checking your details.',
        action: finish
      },
      review: {
        tone: 'review',
        title: 'No orders yet - but you can take them',
        body: 'Your setup is done and we are checking your store over. Share your store link and the first order will appear on the calendar above.',
        action: { kind: 'copy', label: 'Copy store link' }
      }
    })
  }

  if (surface.complete) {
    return {
      tone: 'live',
      title: 'No orders yet - but you can take them',
      body: 'Share your store link and the first order will appear on the calendar above.',
      action: { kind: 'copy', label: 'Copy store link' }
    }
  }

  if (surface.payments === PAYMENTS_DONE) {
    return {
      tone: 'progress',
      title: 'No orders yet - but you can take them',
      body: 'Checkout is open. Every order lands on the calendar above, grouped by the day you make it.',
      action: finish
    }
  }

  if (surface.payments === PAYMENTS_ATTENTION) {
    return {
      tone: 'blocked',
      title: 'No orders yet',
      body: 'Stripe needs something from you before checkout can open. Orders land on the calendar above once it opens.',
      action: finish
    }
  }

  if (surface.payments === PAYMENTS_PENDING) {
    return {
      tone: 'pending',
      title: 'No orders yet',
      body: 'Stripe is still checking your details. Orders land on the calendar above as soon as checkout opens.',
      action: finish
    }
  }

  return {
    tone: 'blocked',
    title: 'No orders yet',
    body: 'Checkout stays closed until payments are connected. Orders land on the calendar above once it opens.',
    action: finish
  }
}

const DRAWER_COPY = {
  todo: {
    title: 'Finish your',
    emphasis: 'setup',
    header:
      'A few things the onboarding journey could not work out for you. Your dashboard stays right behind this panel.',
    footer: 'You can close this and pick it up whenever you like.',
    announcement: ''
  },
  review: {
    title: 'Your setup is',
    emphasis: 'done',
    header: 'We check over every new store by hand, so someone at Bottle is going through yours.',
    footer: 'Nothing here is waiting on you. Close this whenever you like.',
    announcement: 'Every setup step is done. We are checking your store over and will be in touch.'
  },
  reviewed: {
    title: 'Your setup is',
    emphasis: 'done',
    header: 'Everything the onboarding journey could not work out for you is done.',
    footer: 'Nothing here is waiting on you. Close this whenever you like.',
    announcement: 'Every setup step is done. Your setup is complete.'
  }
}

// complete is what decides there are no steps left to read out, and underReview is
// the drawer's own predicate for whether a human has been through them yet. It is
// taken rather than derived here so the header cannot answer that question
// differently from the panel underneath it. An empty announcement is the signal
// that the drawer should speak about the one step that just landed instead.
export function setupDrawerCopy({ complete, underReview } = {}) {
  if (!complete) return DRAWER_COPY.todo
  return underReview ? DRAWER_COPY.review : DRAWER_COPY.reviewed
}
